/**
 * Email Confirmation redirect page
 *
 * Email links point to /confirm-email?token=... on the frontend.
 * This page redirects to the backend GET /api/auth/confirm-email?token=...
 * which verifies the token and redirects to /login?email_confirmed=success|expired|invalid.
 * The Login page handles displaying the confirmation status banner.
 *
 * If accessed without a token, redirects to login.
 */
import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';
import { useLanguage } from '@/store';
import { getAuthTranslation } from '@/utils/translations';

export function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const language = useLanguage();

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      // Redirect to backend confirmation endpoint
      // Backend will verify and redirect to /login?email_confirmed=...
      window.location.href = `/api/auth/confirm-email?token=${encodeURIComponent(token)}`;
    } else {
      // No token — redirect to login
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardContent className="py-12 text-center" role="status" aria-live="polite">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <p className="mt-4 text-sm text-muted-foreground">
            {getAuthTranslation('confirmingEmail', language)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
